import { hasAiAccess } from '../utils/aiAccess';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, BookOpen, Bot, CheckCircle, ChevronDown, ChevronUp, Loader2, Pause, Pencil, Play, Plus, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';

const agentTools = [
  ['search_catalogue', 'Search catalogue', 'Find products, prices and recorded availability.'],
  ['search_business_knowledge', 'Search business knowledge', 'Read published business information and policies.'],
  ['get_customer_context', 'Read customer context', 'Read the customer’s enquiry and pending follow-ups.'],
  ['handover_to_human', 'Hand over to a person', 'Ask your team to help with a conversation.'],
  ['save_enquiry', 'Save enquiries', 'Save clear customer facts; flag uncertain changes for review.'],
  ['schedule_followup', 'Create enquiry tasks', 'Create or update follow-up, confirmation, quotation and preparation tasks.'],
];
const defaultTools = agentTools.slice(0, 4).map(([name]) => name);

function AgentInstructions({ agent }) {
  const [expanded, setExpanded] = useState(false);
  const instructions = agent.instructions || 'Uses your shared business knowledge and default reply style.';
  const compact = instructions.replace(/\s+/g, ' ').trim();
  const long = compact.length > 160;
  return <div className="mt-4">
    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Instructions</p>
    <p id={`agent-instructions-${agent.id}`} className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-600">
      {expanded ? instructions : long ? `${compact.slice(0, 160).trimEnd()}…` : instructions}
    </p>
    {long && <button type="button" aria-expanded={expanded} aria-controls={`agent-instructions-${agent.id}`} onClick={() => setExpanded(!expanded)}
      className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800">
      {expanded ? 'Hide instructions' : 'View instructions'}<span className="sr-only"> for {agent.name}</span>
      {expanded ? <ChevronUp aria-hidden="true" className="h-4 w-4" /> : <ChevronDown aria-hidden="true" className="h-4 w-4" />}
    </button>}
  </div>;
}

function AgentEditor({ agent, busy, error, onSave, onCancel }) {
  const { user } = useAuth();
  const automation = user?.subscription?.features?.automaticCapture === true;
  const [name, setName] = useState(agent?.name || '');
  const [instructions, setInstructions] = useState(agent?.instructions || '');
  const [enabledTools, setEnabledTools] = useState((agent?.enabledTools ?? defaultTools).filter(tool => automation || !['save_enquiry', 'schedule_followup'].includes(tool)));
  return <form aria-labelledby="agent-editor-title" className="space-y-4 rounded-xl border border-primary-200 bg-primary-50/40 p-4 sm:p-5"
    onSubmit={event => { event.preventDefault(); if (!busy && name.trim()) onSave({ name: name.trim(), instructions: instructions.trim(), enabledTools }); }}>
    <div>
      <h4 id="agent-editor-title" className="font-semibold text-gray-900">{agent ? 'Edit agent' : 'Add an AI teammate'}</h4>
      <p className="mt-1 text-sm text-gray-600">Give your agent a clear role and describe how it should help.</p>
    </div>
    <div>
      <label htmlFor="agent-name" className="mb-1.5 block text-sm font-medium text-gray-800">Agent name <span className="font-normal text-gray-500">(required)</span></label>
      <Input id="agent-name" autoFocus required maxLength={80} disabled={busy} value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Sales assistant" />
    </div>
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1">
        <label htmlFor="agent-instructions" className="text-sm font-medium text-gray-800">Instructions <span className="font-normal text-gray-500">(optional)</span></label>
        <span className="text-xs tabular-nums text-gray-500">{instructions.length.toLocaleString()} / 4,000</span>
      </div>
      <textarea id="agent-instructions" rows={5} maxLength={4000} disabled={busy} value={instructions} onChange={event => setInstructions(event.target.value)}
        aria-describedby="agent-instructions-help" placeholder="Help customers choose the right product. Keep replies friendly and concise. Ask a follow-up question when their needs are unclear."
        className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm leading-6 text-gray-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500" />
      <p id="agent-instructions-help" className="mt-1 text-xs leading-5 text-gray-600">Describe the role, tone and when to ask your team for help. Leave blank to use the default reply style.</p>
    </div>
    <fieldset disabled={busy} className="space-y-2">
      <legend className="mb-2 text-sm font-semibold text-gray-800">Tools this agent can use</legend>
      {agentTools.map(([name, label, description]) => <label key={name} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <input type="checkbox" disabled={!automation && ['save_enquiry', 'schedule_followup'].includes(name)} className="mt-1 h-4 w-4 shrink-0" checked={enabledTools.includes(name)} onChange={event => setEnabledTools(current => event.target.checked ? [...current, name] : current.filter(tool => tool !== name))} />
        <span className="min-w-0"><span className="block text-sm font-medium text-gray-800">{label}{!automation && ['save_enquiry', 'schedule_followup'].includes(name) ? ' · AI Growth' : ''}</span><span className="block text-xs leading-5 text-gray-600">{description}</span></span>
      </label>)}
      <p className="text-xs leading-5 text-gray-600">These tools control actions taken by this agent. AI Growth is required for automatic enquiry saves and tasks. Workspace enquiry capture is managed separately in WhatsApp settings.</p>
    </fieldset>
    {error && <Alert role="alert">{error}</Alert>}
    <div className="flex flex-wrap items-center gap-2 border-t border-primary-100 pt-4">
      <Button type="submit" size="sm" disabled={busy || !name.trim()} className="inline-flex items-center justify-center gap-2">
        {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
        {busy ? 'Saving…' : agent ? 'Save changes' : 'Add agent'}
      </Button>
      <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
    </div>
  </form>;
}

export default function TeamAgents() {
  const { user } = useAuth();
  const client = useQueryClient();
  const scope = user?.workspaceOwnerId || user?.id;
  const queryKey = ['workspace', 'agents', scope];
  const query = useQuery({ queryKey, queryFn: async () => (await workspaceAPI.getAgents()).data, retry: false, enabled: hasAiAccess(user) });
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const returnFocus = useRef(null);
  const owner = user?.role === 'owner';
  const agents = query.data?.agents || [];
  const activeCount = agents.filter(agent => agent.active).length;

  useEffect(() => {
    if (!editor && returnFocus.current) {
      returnFocus.current.focus();
      returnFocus.current = null;
    }
  }, [editor]);

  const openEditor = (event, agent = null) => {
    returnFocus.current = event.currentTarget;
    setError('');
    setNotice('');
    setEditor({ agent });
  };
  const closeEditor = () => { setEditor(null); setError(''); };
  const act = async (key, action, message) => {
    if (busy) return false;
    setBusy(key);
    setError('');
    setNotice('');
    try {
      await action();
      await client.invalidateQueries({ queryKey });
      await client.invalidateQueries({ queryKey: ['whatsapp'] });
      await client.invalidateQueries({ queryKey: ['salesReport'] });
      setNotice(message);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this change. Please try again.');
      return false;
    } finally { setBusy(null); }
  };
  const saveAgent = async values => {
    const saved = await act('save', () => editor.agent
      ? workspaceAPI.updateAgent(editor.agent.id, values)
      : workspaceAPI.createAgent(values), `${values.name} ${editor.agent ? 'updated' : 'added'}.`);
    if (saved) closeEditor();
  };
  const renderEditor = () => <AgentEditor agent={editor.agent} busy={busy === 'save'} error={error} onSave={saveAgent} onCancel={closeEditor} />;

  if (!hasAiAccess(user) || query.data?.enabled === false) return null;
  return <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" aria-labelledby="team-agents-title">
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600"><Bot aria-hidden="true" className="h-6 w-6" /></span>
          <div>
            <h3 id="team-agents-title" className="text-lg font-semibold text-gray-900">AI agents</h3>
            <p className="mt-1 max-w-lg text-sm leading-6 text-gray-600">AI teammates with their own roles and instructions, ready to help with enquiries and WhatsApp conversations.</p>
          </div>
        </div>
        {owner && <Button type="button" size="sm" disabled={Boolean(busy || editor) || !query.data} onClick={event => openEditor(event)} className="inline-flex shrink-0 items-center justify-center gap-2">
          <Plus aria-hidden="true" className="h-4 w-4" />Add AI agent
        </Button>}
      </div>

      {agents.length > 0 && <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
        <span className="font-medium text-gray-800">{agents.length} {agents.length === 1 ? 'agent' : 'agents'}</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-green-600" />{activeCount} active</span>
        {agents.length > activeCount && <span>{agents.length - activeCount} paused</span>}
      </div>}
      {!owner && <p className="text-sm text-gray-500">Your workspace owner manages agents and their instructions.</p>}
      {notice && <Alert variant="success" role="status" className="flex items-start gap-2"><CheckCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" /><span>{notice}</span></Alert>}
      {error && !editor && <Alert role="alert">{error}</Alert>}
      {query.isError && <Alert role="alert" className="flex flex-wrap items-center justify-between gap-2">
        <span>Could not load AI agents. Please try again.</span>
        <Button type="button" size="sm" variant="secondary" disabled={query.isFetching} onClick={() => query.refetch()}>{query.isFetching ? 'Retrying…' : 'Try again'}</Button>
      </Alert>}
      {query.isPending && <div role="status" className="flex items-center gap-2 rounded-xl bg-gray-50 p-5 text-sm text-gray-600"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />Loading AI agents…</div>}
      {editor && !editor.agent && owner && renderEditor()}

      {agents.length > 0 && <ul className="space-y-3" aria-label="AI agents">
        {agents.map(agent => <li key={agent.id} className="min-w-0 rounded-xl border border-gray-200 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${agent.active ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500'}`}><Bot aria-hidden="true" className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <h4 className="break-words font-semibold text-gray-900">{agent.name}</h4>
              <p className="mt-0.5 text-xs text-gray-500">AI teammate</p>
            </div>
            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${agent.active ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {agent.active ? <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-green-600" /> : <Pause aria-hidden="true" className="h-3 w-3" />}{agent.active ? 'Active' : 'Paused'}
            </span>
          </div>
          {editor?.agent?.id !== agent.id && <><AgentInstructions agent={agent} /><p className="mt-3 text-xs leading-5 text-gray-500">Tools: {agentTools.filter(([name]) => (agent.enabledTools ?? defaultTools).includes(name)).map(([, label]) => label).join(", ") || "None selected"}</p></>}
          <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">{agent.active ? 'Available for assignment' : 'Resume to make this agent available'}</p>
            {owner && <div className="flex gap-2">
              <Button type="button" size="sm" variant="secondary" disabled={Boolean(busy || editor)} aria-label={`Edit ${agent.name}`} aria-expanded={editor?.agent?.id === agent.id} onClick={event => openEditor(event, agent)} className="inline-flex items-center gap-2 disabled:opacity-50"><Pencil aria-hidden="true" className="h-3.5 w-3.5" />Edit</Button>
              <Button type="button" size="sm" variant="ghost" disabled={Boolean(busy || editor)} aria-label={`${agent.active ? 'Pause' : 'Resume'} ${agent.name}`} onClick={() => act(agent.id, () => workspaceAPI.updateAgent(agent.id, { active: !agent.active }), `${agent.name} ${agent.active ? 'paused' : 'resumed'}.`)} className="inline-flex items-center gap-2 disabled:opacity-50">
                {busy === agent.id ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : agent.active ? <Pause aria-hidden="true" className="h-4 w-4" /> : <Play aria-hidden="true" className="h-4 w-4" />}
                {busy === agent.id ? 'Updating…' : agent.active ? 'Pause' : 'Resume'}
              </Button>
            </div>}
          </div>
          {editor?.agent?.id === agent.id && owner && <div className="mt-4">{renderEditor()}</div>}
        </li>)}
      </ul>}
      {!query.isPending && !query.isError && agents.length === 0 && !editor && <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-8 text-center">
        <Bot aria-hidden="true" className="mx-auto mb-3 h-8 w-8 text-primary-600" />
        <h4 className="font-semibold text-gray-900">Your first AI teammate starts here</h4>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-600">{owner ? 'Use Add AI agent to create a sales or support assistant. Give it a role, then assign it to an enquiry or conversation.' : 'Once your workspace owner adds an agent, it will appear here.'}</p>
      </div>}
      {agents.length > 0 && <p className="text-xs leading-5 text-gray-500">Assign an active agent from an enquiry or conversation’s assignee selector. Automatic replies are controlled separately in WhatsApp settings.</p>}
    </div>
    <div className="grid border-t border-gray-200 bg-gray-50 sm:grid-cols-2">
      <Link to="/settings/business-knowledge" className="group flex items-center gap-3 p-4 transition-colors hover:bg-gray-100 sm:px-6">
        <BookOpen aria-hidden="true" className="h-5 w-5 shrink-0 text-gray-500" />
        <div className="min-w-0 flex-1"><span className="text-sm font-medium text-gray-800 group-hover:text-primary-700">Shared knowledge</span><p className="mt-0.5 text-xs text-gray-500">Business information your agents use</p></div>
        <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
      </Link>
      <Link to="/settings/whatsapp" className="group flex items-center gap-3 border-t border-gray-200 p-4 transition-colors hover:bg-gray-100 sm:border-l sm:border-t-0 sm:px-6">
        <Settings2 aria-hidden="true" className="h-5 w-5 shrink-0 text-gray-500" />
        <div className="min-w-0 flex-1"><span className="text-sm font-medium text-gray-800 group-hover:text-primary-700">Reply settings</span><p className="mt-0.5 text-xs text-gray-500">Draft review and automatic replies</p></div>
        <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
      </Link>
    </div>
  </section>;
}
